import { createContext, useContext, type Dispatch } from "react";
import type { AdminApi } from "@/data/adminApi";
import type { AuthMode, CurrentUser } from "@/domain/permissions";
import type { UiAction, UiState } from "./uiState";
import type { AppData } from "./useAppData";

export interface AppContextValue {
  authMode: AuthMode;
  user: CurrentUser | null;
  data: AppData;
  /** Present for superadmins and local admins in individual mode; the API scopes local admins. */
  adminApi: AdminApi | null;
  signOut(): void;
}

export const AppContext = createContext<AppContextValue | null>(null);
export const UiStateContext = createContext<UiState | null>(null);
export const UiDispatchContext = createContext<Dispatch<UiAction> | null>(null);

function required<T>(value: T | null, name: string): T {
  if (value === null) throw new Error(`${name} must be used inside <App>`);
  return value;
}

export function useApp(): AppContextValue {
  return required(useContext(AppContext), "useApp");
}

export function useUi(): UiState {
  return required(useContext(UiStateContext), "useUi");
}

export function useUiDispatch(): Dispatch<UiAction> {
  return required(useContext(UiDispatchContext), "useUiDispatch");
}
